// Parent Window Analytics Provider
//
// This provider forwards analytics events via postMessage(), enabling host pages
// to receive analytics events from the embedded Rose widget (shadow DOM or iframe).
//
// The widget can be embedded in two ways:
// 1. Shadow DOM (most common): Widget runs in same window, postMessage to self
// 2. Iframe (rare): Widget runs in iframe, postMessage to parent window
//
// In both cases, the host page listens with:
//   window.addEventListener('message', handler)
//
// SECURITY NOTE:
// - Default targetOrigin is '*' (any origin) for ease of setup
// - For production, recommend setting specific targetOrigin via config
// - Only non-sensitive events are forwarded by default (USER_ACTION, CONVERSION)

import { logger } from '../../utils/core/logger';
import type { AnalyticsEvent, EventCategory } from '../types';
import { EventCategories } from '../types';
import { SimpleAnalyticsProvider } from './base';

/**
 * Configuration for window event forwarding
 */
export interface ParentWindowConfig {
    /** Whether the provider is enabled */
    enabled: boolean;
    /** Target origin for postMessage (default: '*' for any origin) */
    targetOrigin?: string;
    /** Event categories to forward (default: ['user_action', 'conversion']) */
    eventCategories?: EventCategory[];
}

/**
 * Message format sent via postMessage
 */
export interface ParentWindowEventMessage {
    /** Message type identifier for filtering in host page */
    type: 'rose-analytics-event';
    /** Event payload */
    payload: {
        /** Event type identifier (e.g., 'widget_impression', 'message_sent') */
        eventType: string;
        /** Event category */
        category: string;
        /** Event-specific properties */
        properties: Record<string, unknown>;
        /** Event context */
        context: {
            sessionId: string | undefined;
            domain: string | undefined;
            timestamp: number;
        };
    };
}

/**
 * Default event categories to forward
 * - USER_ACTION: User interactions (clicks, typing, sending messages)
 * - CONVERSION: Conversion events (CTA clicks, form submissions)
 * - SYSTEM: System events (widget impression, initialization)
 */
const DEFAULT_EVENT_CATEGORIES: EventCategory[] = [
    EventCategories.USER_ACTION,
    EventCategories.CONVERSION,
    EventCategories.SYSTEM,
];

/**
 * Parent Window Analytics Provider
 *
 * Forwards analytics events via postMessage() for host page integration.
 * Works with both shadow DOM (same window) and iframe (parent window) embeddings.
 *
 * Usage:
 * 1. Enable via the unified analytics config (`analytics.parent_window_events`) in Supabase
 * 2. Host page listens for 'message' events with type 'rose-analytics-event'
 *
 * Example host page listener:
 * ```javascript
 * window.addEventListener('message', (event) => {
 *     if (event.data?.type === 'rose-analytics-event') {
 *         console.log('Rose event:', event.data.payload);
 *         // Forward to your analytics system (e.g., Pennylane)
 *     }
 * });
 * ```
 */
export class ParentWindowProvider extends SimpleAnalyticsProvider {
    public readonly name = 'parent-window';

    private config: ParentWindowConfig;
    private allowedCategories: Set<EventCategory>;

    constructor(config: ParentWindowConfig) {
        super(5000); // 5 second timeout (though postMessage is synchronous)
        this.config = config;
        this.allowedCategories = new Set(config.eventCategories || DEFAULT_EVENT_CATEGORIES);

        logger.debug('🪟 ParentWindowProvider initialized:', {
            targetOrigin: this.getTargetOrigin(),
            eventCategories: Array.from(this.allowedCategories),
            isInIframe: this.isInIframe(),
        });
    }

    /**
     * Get the target origin for postMessage
     */
    private getTargetOrigin(): string {
        return this.config.targetOrigin || '*';
    }

    /**
     * Check if we're in an iframe context
     */
    private isInIframe(): boolean {
        try {
            return window.self !== window.top;
        } catch {
            // If we can't access window.top due to cross-origin restrictions,
            // we're definitely in an iframe
            return true;
        }
    }

    /**
     * Convert analytics properties to a postMessage-safe payload.
     * This prevents DataCloneError when payloads contain DOM nodes or other non-cloneable values.
     */
    private sanitizeForPostMessage(value: unknown, seen = new WeakSet<object>()): unknown {
        if (
            value === null ||
            value === undefined ||
            typeof value === 'string' ||
            typeof value === 'number' ||
            typeof value === 'boolean'
        ) {
            return value;
        }

        if (typeof value === 'bigint') {
            return value.toString();
        }

        if (typeof value === 'function' || typeof value === 'symbol') {
            return undefined;
        }

        if (value instanceof Date) {
            return value.toISOString();
        }

        if (value instanceof Error) {
            return {
                name: value.name,
                message: value.message,
                stack: value.stack,
            };
        }

        if (typeof Node !== 'undefined' && value instanceof Node) {
            if (value instanceof HTMLElement) {
                return `[HTMLElement:${value.tagName}]`;
            }
            return `[Node:${value.nodeName}]`;
        }

        if (typeof Event !== 'undefined' && value instanceof Event) {
            return {
                type: value.type,
            };
        }

        if (Array.isArray(value)) {
            return value
                .map((item) => this.sanitizeForPostMessage(item, seen))
                .filter((item) => item !== undefined);
        }

        if (typeof value === 'object') {
            const objectValue = value as Record<string, unknown>;

            if (seen.has(objectValue)) {
                return '[Circular]';
            }
            seen.add(objectValue);

            const sanitized: Record<string, unknown> = {};
            Object.entries(objectValue).forEach(([key, nestedValue]) => {
                const safeValue = this.sanitizeForPostMessage(nestedValue, seen);
                if (safeValue !== undefined) {
                    sanitized[key] = safeValue;
                }
            });
            return sanitized;
        }

        return String(value);
    }

    /**
     * Filter events based on configured categories
     */
    public eventFilter(event: AnalyticsEvent): boolean {
        // Only accept events with categories that are in our allowed list
        if (!event.category) {
            return false;
        }
        return this.allowedCategories.has(event.category);
    }

    /**
     * Check if the provider is enabled
     */
    public isEnabled(): boolean {
        // Provider is disabled if not configured as enabled
        if (!this.config.enabled) {
            return false;
        }

        return super.isEnabled();
    }

    /**
     * Send event via postMessage
     * - In iframe context: sends to parent window
     * - In same-window context (shadow DOM): sends to same window
     */
    protected async sendEventImpl(event: AnalyticsEvent): Promise<void> {
        // Construct message
        const message: ParentWindowEventMessage = {
            type: 'rose-analytics-event',
            payload: {
                eventType: event.type,
                category: event.category || 'unknown',
                properties: this.sanitizeForPostMessage(event.properties) as Record<
                    string,
                    unknown
                >,
                context: {
                    sessionId: event.context.sessionId,
                    domain: event.context.domain,
                    timestamp: event.timestamp,
                },
            },
        };

        // Send via postMessage
        // - In iframe: window.parent is the parent window
        // - In same window (shadow DOM): window.parent === window, still works
        try {
            const targetWindow = this.isInIframe() ? window.parent : window;
            targetWindow.postMessage(message, this.getTargetOrigin());
            logger.debug('🪟 Sent event via postMessage:', {
                eventType: event.type,
                category: event.category,
                targetOrigin: this.getTargetOrigin(),
                isInIframe: this.isInIframe(),
            });
        } catch (error) {
            logger.error('🪟 Failed to send event via postMessage:', error);
            throw error;
        }
    }

    /**
     * Update provider configuration
     */
    public updateConfig(config: Partial<ParentWindowConfig>): void {
        if (config.enabled !== undefined) {
            this.config.enabled = config.enabled;
        }
        if (config.targetOrigin !== undefined) {
            this.config.targetOrigin = config.targetOrigin;
        }
        if (config.eventCategories !== undefined) {
            this.allowedCategories = new Set(config.eventCategories);
        }

        logger.debug('🪟 ParentWindowProvider config updated:', {
            enabled: this.config.enabled,
            targetOrigin: this.getTargetOrigin(),
            eventCategories: Array.from(this.allowedCategories),
        });
    }
}
